/**
 * SheetLoadingOverlay
 *
 * Semi-transparent overlay rendered on top of sheet content while an action is
 * in flight, dimming the content and centering a loading indicator over it.
 *
 * Mirrors Flutter's withValues(alpha: .7) modal-background overlay, which is the
 * same construct in both sheets that use it:
 *   - lib/presentation/bottom_sheet/subscribe_user_bottom_sheet.dart:572-592
 *   - lib/presentation/bottom_sheet/review_exclusive_subscription_bottom_sheet.dart:231-251
 *
 * The host sheet content positions this overlay — it renders absolutely inset to
 * its nearest positioned ancestor, so that ancestor must be `position: relative`.
 */
import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface SheetLoadingOverlayProps {
  /** Stable `data-qa-id` for the overlay, namespaced by the host sheet. */
  qaId: string;
}

export const SheetLoadingOverlay: React.FC<SheetLoadingOverlayProps> = ({ qaId }) => {
  const { theme } = useTheme();

  return (
    <div
      {...qaAttr({ id: qaId, status: 'processing' })}
      className="flex items-center justify-center"
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        // 'B3' hex alpha ≈ 70% opacity, matching Flutter's withValues(alpha: .7).
        backgroundColor: `${theme.modalBackground}B3`,
        zIndex: 1,
      }}
    >
      <ExpauseLoadingIndicator />
    </div>
  );
};
